import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MdTask,
  MdDone,
  MdTrendingUp,
  MdHourglassEmpty,
  MdPerson,
  MdPriorityHigh,
  MdAccessTime,
  MdArrowForward,
  MdCheckCircle,
  MdPeople,
  MdCalendarToday,
  MdRateReview,
  MdQuiz,
} from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  format,
  formatDistanceToNow,
  isBefore,
  isAfter,
  addDays,
} from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  AreaChart,
  Area,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import Lottie from "lottie-react";
import taskPulseAnim from "../assets/lottie/taskPulse.json";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";
const PALETTE = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];
const initials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const aColor = (name) => PALETTE[(name?.charCodeAt(0) || 0) % PALETTE.length];

const UserAvatar = ({ user, size = 32 }) => {
  const src = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.3,
        background: src ? "transparent" : aColor(user?.name),
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={user?.name}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = initials(user?.name);
          }}
        />
      ) : (
        initials(user?.name)
      )}
    </div>
  );
};

/* Recharts tooltip */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "8px 13px",
        boxShadow: "0 4px 14px rgba(0,0,0,.1)",
        fontSize: "0.8rem",
      }}
    >
      {label && <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

/* Mini horizontal progress bar */
const MiniBar = ({ value, max, color }) => (
  <div
    style={{
      flex: 1,
      height: 6,
      background: "var(--surface3)",
      borderRadius: 99,
      overflow: "hidden",
    }}
  >
    <div
      style={{
        height: "100%",
        width: `${max ? Math.round((value / max) * 100) : 0}%`,
        background: color,
        borderRadius: 99,
        transition: "width .4s ease",
      }}
    />
  </div>
);

/* Stat card with animated icon */
const StatCard = ({ icon, value, label, bg, color, sub, to, lottie }) => {
  const Wrapper = to ? Link : "div";
  const wrapperProps = to
    ? {
        to,
        className: "card stat-card",
        style: { textDecoration: "none", color: "inherit" },
      }
    : { className: "card stat-card" };

  return (
    <Wrapper {...wrapperProps}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div
          className="stat-icon dashboard-stat-icon"
          style={{ background: bg, marginBottom: 0 }}
        >
          {lottie ? (
            <Lottie
              animationData={lottie}
              loop
              autoplay
              style={{ width: 32, height: 32 }}
            />
          ) : (
            <span style={{ color, fontSize: 22, display: "flex" }}>{icon}</span>
          )}
        </div>
        {to && (
          <span style={{ display: "flex", color: "var(--text-muted)" }}>
            <MdArrowForward size={16} />
          </span>
        )}
      </div>
      <div className="stat-value" style={{ marginTop: 14 }}>
        {value}
      </div>
      <div className="stat-label">{label}</div>
      {sub && (
        <div
          style={{ fontSize: "0.72rem", color, fontWeight: 600, marginTop: 4 }}
        >
          {sub}
        </div>
      )}
    </Wrapper>
  );
};

/* ── Main ────────────────────────────────────────────────── */
const Dashboard = () => {
  const { user, isAdmin, isSuperAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignedAssessments, setAssignedAssessments] = useState([]);

  const now = new Date();
  const hour = now.getHours();
  const greet =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    const taskReq = isAdmin ? api.get("/tasks/admin/all") : api.get("/tasks");
    const reqs = [taskReq, api.get("/assessments/assigned")];
    if (isAdmin) reqs.push(api.get("/users"));
    Promise.all(reqs)
      .then(([t, a, u]) => {
        setTasks(t.data.data || []);
        setAssignedAssessments(a?.data?.data || []);
        if (u) setUsers(u.data.data || []);
      })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  /* ── Derived numbers ── */
  const isSameDay = (a, b) => a.toDateString() === b.toDateString();
  const idOf = (value) =>
    value && typeof value === "object" ? value._id || value.id : value;

  const todo = tasks.filter((t) => t.status === "todo").length;
  const inProg = tasks.filter((t) => t.status === "in-progress").length;
  const review = tasks.filter((t) => t.status === "review").length;
  const done = tasks.filter((t) => t.status === "done").length;
  const total = tasks.length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  const overdue = tasks.filter(
    (t) =>
      t.deadline && t.status !== "done" && isBefore(new Date(t.deadline), now),
  );
  const dueSoon = tasks.filter(
    (t) =>
      t.deadline &&
      t.status !== "done" &&
      isAfter(new Date(t.deadline), now) &&
      isBefore(new Date(t.deadline), addDays(now, 3)),
  );

  const completedToday = tasks.filter(
    (t) =>
      t.status === "done" &&
      t.updatedAt &&
      isSameDay(new Date(t.updatedAt), now),
  ).length;

  const dueToday = tasks.filter(
    (t) =>
      t.deadline && t.status !== "done" && isSameDay(new Date(t.deadline), now),
  ).length;

  const myPending = tasks.filter(
    (t) =>
      t.status !== "done" &&
      (t.createdBy?._id === user?._id || t.assignedTo?._id === user?._id),
  ).length;

  const upcoming = tasks
    .filter((t) => t.status !== "done" && t.deadline)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
    .slice(0, 6);
  const recent = [...tasks]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 6);
  const selfAssigned = tasks.filter((t) => {
    const createdId = idOf(t.createdBy);
    const assignedId = idOf(t.assignedTo);
    return !!createdId && !!assignedId && createdId === assignedId;
  }).length;
  const assignedToOthers = tasks.filter((t) => {
    const createdId = idOf(t.createdBy);
    const assignedId = idOf(t.assignedTo);
    return !!createdId && !!assignedId && createdId !== assignedId;
  }).length;
  const unassigned = tasks.filter((t) => !idOf(t.assignedTo)).length;
  const completedOnTime = tasks.filter((t) => {
    if (t.status !== "done") return false;
    if (!t.deadline) return true;
    const completedAt = t.updatedAt
      ? new Date(t.updatedAt)
      : new Date(t.createdAt);
    return !isAfter(completedAt, new Date(t.deadline));
  }).length;
  const onTimeRate = done ? Math.round((completedOnTime / done) * 100) : 0;
  const dueNextThreeDays = tasks.filter((t) => {
    if (!t.deadline || t.status === "done") return false;
    const deadline = new Date(t.deadline);
    return (
      !isSameDay(deadline, now) &&
      isAfter(deadline, now) &&
      isBefore(deadline, addDays(now, 3))
    );
  }).length;
  const onTrack = tasks.filter((t) => {
    if (!t.deadline || t.status === "done") return false;
    return isAfter(new Date(t.deadline), addDays(now, 3));
  }).length;
  const openWithoutDeadline = tasks.filter(
    (t) => !t.deadline && t.status !== "done",
  ).length;

  /* ── Chart data ── */
  const statusData = [
    { name: "To Do", value: todo, color: "#64748b" },
    { name: "In Progress", value: inProg, color: "#3b82f6" },
    { name: "Review", value: review, color: "#f59e0b" },
    { name: "Done", value: done, color: "#10b981" },
  ];

  const priorityData = [
    {
      name: "Critical",
      value: tasks.filter((t) => t.priority === "critical").length,
      color: "#ef4444",
    },
    {
      name: "High",
      value: tasks.filter((t) => t.priority === "high").length,
      color: "#f97316",
    },
    {
      name: "Medium",
      value: tasks.filter((t) => t.priority === "medium").length,
      color: "#f59e0b",
    },
    {
      name: "Low",
      value: tasks.filter((t) => t.priority === "low").length,
      color: "#10b981",
    },
  ];

  /* tasks created per day — last 7 days */
  const activity = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const label = format(d, "MMM d");
    return {
      date: label,
      tasks: tasks.filter(
        (t) => format(new Date(t.createdAt), "MMM d") === label,
      ).length,
    };
  });

  const flowData = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const label = format(d, "MMM d");
    return {
      date: label,
      created: tasks.filter(
        (t) => format(new Date(t.createdAt), "MMM d") === label,
      ).length,
      completed: tasks.filter(
        (t) =>
          t.status === "done" &&
          t.updatedAt &&
          format(new Date(t.updatedAt), "MMM d") === label,
      ).length,
    };
  });

  const assignmentData = [
    { name: "Self Assigned", value: selfAssigned, color: "#6366f1" },
    { name: "Assigned to Others", value: assignedToOthers, color: "#06b6d4" },
    { name: "Unassigned", value: unassigned, color: "#f59e0b" },
  ];

  const deadlineHealthData = [
    { name: "Overdue", value: overdue.length, color: "#ef4444" },
    { name: "Due Today", value: dueToday, color: "#f97316" },
    { name: "Next 3 Days", value: dueNextThreeDays, color: "#eab308" },
    { name: "On Track", value: onTrack, color: "#22c55e" },
    { name: "No Deadline", value: openWithoutDeadline, color: "#64748b" },
  ];

  /* per-user task count (admin) */
  const perUser = isAdmin
    ? users
        .map((u) => ({
          user: u,
          name: u.name.split(" ")[0],
          count: tasks.filter(
            (t) => t.assignedTo?._id === u._id || t.createdBy?._id === u._id,
          ).length,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 7)
    : [];
  const maxCount = perUser[0]?.count || 1;
  const assigneePerformance = isAdmin
    ? users
        .map((u) => {
          const assigned = tasks.filter((t) => idOf(t.assignedTo) === u._id);
          const completed = assigned.filter((t) => t.status === "done").length;
          return {
            name: u.name.split(" ")[0],
            assigned: assigned.length,
            completed,
            open: assigned.length - completed,
          };
        })
        .filter((row) => row.assigned > 0)
        .sort((a, b) => b.assigned - a.assigned)
        .slice(0, 6)
    : [];

  if (loading)
    return (
      <div className="loading">
        <div className="spinner" />
        <span className="loading-text">Loading...</span>
      </div>
    );

  return (
    <main className="page">
      {/* ── Header ── */}
      <div className="page-header" style={{ flexWrap: "wrap", gap: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            flex: 1,
            minWidth: 0,
          }}
        >
          <div
            className="dashboard-welcome-icon"
            style={{
              width: 72,
              height: 72,
              flexShrink: 0,
              borderRadius: 16,
              background: "var(--primary-light)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 36,
            }}
          >
            👋
          </div>
          <div>
            <h1 className="page-title">
              {greet}, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="page-subtitle">
              {format(now, "EEEE, MMMM d, yyyy")} — here's your overview
            </p>
          </div>
        </div>
        <Link
          to={isAdmin ? "/admin/tasks" : "/tasks"}
          className="btn btn-primary"
        >
          <MdTask size={17} /> {isAdmin ? "Manage Staff Tasks" : "My Tasks"}
        </Link>
      </div>

      {assignedAssessments.length > 0 && (
        <div
          className="card"
          style={{ marginBottom: 24, padding: "16px 18px" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            <h3
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: "1rem",
              }}
            >
              <MdQuiz /> Assigned Assessments
            </h3>
            <Link to="/assessments" className="btn btn-sm btn-secondary">
              View all
            </Link>
          </div>
          <div style={{ display: "grid", gap: 8 }}>
            {assignedAssessments.slice(0, 4).map(({ assessment, attempt }) => (
              <div
                key={assessment._id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid var(--border)",
                  background: "var(--bg)",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <strong>{assessment.title}</strong>
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "var(--text-muted)",
                      marginTop: 2,
                    }}
                  >
                    {assessment.durationMinutes} min · {attempt.status.replace("_", " ")}
                    {["submitted", "evaluated"].includes(attempt.status)
                      ? ` · Score ${attempt.totalScore}/${assessment.totalMarks}`
                      : ""}
                  </div>
                </div>
                <Link
                  to={`/assessments/${assessment._id}/take`}
                  className="btn btn-sm btn-primary"
                >
                  {attempt.status === "assigned"
                    ? "Start"
                    : attempt.status === "in_progress"
                      ? "Continue"
                      : "View"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          icon={<MdHourglassEmpty />}
          value={todo}
          label="To Do"
          bg="#f1f5f9"
          color="#64748b"
          to={isAdmin ? "/admin/tasks?quick=todo" : "/tasks"}
        />
        <StatCard
          icon={<MdTrendingUp />}
          value={inProg}
          label="In Progress"
          bg="#dbeafe"
          color="#3b82f6"
          to={isAdmin ? "/admin/tasks?quick=in-progress" : "/tasks"}
        />
        <StatCard
          icon={<MdRateReview />}
          value={review}
          label="In Review"
          bg="#fef3c7"
          color="#f59e0b"
          to={isAdmin ? "/admin/tasks?quick=review" : "/tasks"}
        />
        <StatCard
          icon={<MdDone />}
          value={done}
          label="Completed"
          bg="#d1fae5"
          color="#10b981"
          lottie={taskPulseAnim}
          sub={`${pct}% completion rate`}
          to={isAdmin ? "/admin/tasks?quick=done" : "/tasks"}
        />
      </div>

      {/* ── Productivity widgets ── */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          icon={<MdCheckCircle />}
          value={completedToday}
          label="Tasks Completed Today"
          bg="#ecfdf5"
          color="#16a34a"
          to={isAdmin ? "/admin/tasks?quick=completed-today" : undefined}
        />
        <StatCard
          icon={<MdCalendarToday />}
          value={dueToday}
          label="Tasks Due Today"
          bg="#eff6ff"
          color="#2563eb"
          to={isSuperAdmin ? "/admin/tasks?quick=due-today" : undefined}
        />
        <StatCard
          icon={<MdPriorityHigh />}
          value={overdue.length}
          label="Overdue Tasks"
          bg="#fef2f2"
          color="#dc2626"
          to={isSuperAdmin ? "/admin/tasks?quick=overdue" : undefined}
        />
        <StatCard
          icon={<MdTask />}
          value={myPending}
          label="My Pending Tasks"
          bg="#f5f3ff"
          color="#7c3aed"
          to={isAdmin ? "/admin/tasks?quick=my-pending" : undefined}
        />
      </div>

      {/* ── Extra KPI widgets ── */}
      <div className="grid grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          icon={<MdPerson />}
          value={selfAssigned}
          label="Self Assigned"
          bg="#eef2ff"
          color="#4f46e5"
          sub="Tasks kept with creator"
        />
        <StatCard
          icon={<MdPeople />}
          value={assignedToOthers}
          label="Assigned to Others"
          bg="#ecfeff"
          color="#0891b2"
          sub="Delegated workload"
        />
        <StatCard
          icon={<MdHourglassEmpty />}
          value={unassigned}
          label="Unassigned Tasks"
          bg="#fff7ed"
          color="#ea580c"
          sub="Need ownership"
        />
        <StatCard
          icon={<MdCheckCircle />}
          value={`${onTimeRate}%`}
          label="On-Time Completion"
          bg="#ecfdf5"
          color="#059669"
          sub={`${completedOnTime}/${done} closed on time`}
        />
      </div>

      {/* ── Alert banners ── */}
      {(overdue.length > 0 || dueSoon.length > 0) && (
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 24,
            flexWrap: "wrap",
          }}
        >
          {overdue.length > 0 && (
            <Link
              to={
                isSuperAdmin
                  ? "/admin/tasks?quick=overdue"
                  : "/tasks?filter=overdue"
              }
              style={{
                flex: 1,
                minWidth: 220,
                background: "#fee2e2",
                border: "1px solid #fca5a5",
                borderRadius: 12,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: "#fecaca",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MdPriorityHigh color="#dc2626" size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: "#dc2626",
                  }}
                >
                  {overdue.length} Overdue Task{overdue.length > 1 ? "s" : ""}
                </div>
                <div style={{ fontSize: "0.75rem", color: "#b91c1c" }}>
                  Need immediate attention
                </div>
              </div>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#dc2626",
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                }}
              >
                View →
              </span>
            </Link>
          )}
          {dueSoon.length > 0 && (
            <Link
              to={
                isSuperAdmin
                  ? "/admin/tasks?quick=due-within-days"
                  : "/tasks?filter=due-soon"
              }
              style={{
                flex: 1,
                minWidth: 220,
                background: "#fef3c7",
                border: "1px solid #fcd34d",
                borderRadius: 12,
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                textDecoration: "none",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: "#fde68a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MdAccessTime color="#d97706" size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    color: "#d97706",
                  }}
                >
                  {dueSoon.length} Due within 3 days
                </div>
                <div style={{ fontSize: "0.75rem", color: "#92400e" }}>
                  Stay on track
                </div>
              </div>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  color: "#d97706",
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                }}
              >
                View →
              </span>
            </Link>
          )}
        </div>
      )}

      {/* ── Chart row ── */}
      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        {/* Donut — status */}
        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Task Status
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            Current breakdown
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="48%"
                innerRadius={52}
                outerRadius={76}
                paddingAngle={3}
                dataKey="value"
                label={({ percent }) =>
                  percent > 0 ? `${Math.round(percent * 100)}%` : ""
                }
                labelLine={false}
              >
                {statusData.map((e, i) => (
                  <Cell key={i} fill={e.color} />
                ))}
              </Pie>
              <Tooltip content={<ChartTip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "0.72rem" }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar — priority */}
        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            By Priority
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            Task distribution
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart
              data={priorityData}
              barSize={32}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} name="Tasks">
                {priorityData.map((e, i) => (
                  <Cell key={i} fill={e.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Area — 7-day activity */}
        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Activity
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            Tasks created — last 7 days
          </div>
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart
              data={activity}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <defs>
                <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<ChartTip />} />
              <Area
                type="monotone"
                dataKey="tasks"
                stroke="#6366f1"
                strokeWidth={2.5}
                fill="url(#actGrad)"
                name="Tasks"
                dot={{ r: 3, fill: "#6366f1" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Additional analytics ── */}
      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Task Flow Trend
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            Created vs completed over the last 7 days
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart
              data={flowData}
              margin={{ top: 4, right: 10, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<ChartTip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "0.72rem" }}
              />
              <Line
                type="monotone"
                dataKey="created"
                name="Created"
                stroke="#6366f1"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="completed"
                name="Completed"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Assignment Mix
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            How tasks are distributed right now
          </div>
          {assignmentData.some((item) => item.value > 0) ? (
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie
                  data={assignmentData}
                  cx="50%"
                  cy="48%"
                  innerRadius={50}
                  outerRadius={76}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ percent }) =>
                    percent > 0 ? `${Math.round(percent * 100)}%` : ""
                  }
                  labelLine={false}
                >
                  {assignmentData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<ChartTip />} />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: "0.72rem" }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: 210,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--text-muted)",
                fontSize: "0.85rem",
              }}
            >
              No assignment data yet
            </div>
          )}
        </div>

        <div className="card card-body">
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Deadline Health
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            Open task risk by deadline window
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart
              data={deadlineHealthData}
              barSize={26}
              margin={{ top: 4, right: 4, left: -18, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip content={<ChartTip />} />
              <Bar dataKey="value" name="Tasks" radius={[6, 6, 0, 0]}>
                {deadlineHealthData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Middle row ── */}
      <div className="grid grid-2" style={{ marginBottom: 24 }}>
        {/* Upcoming deadlines */}
        <div className="card">
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              Upcoming Deadlines
            </span>
            <Link to="/tasks" className="btn btn-secondary btn-sm">
              View All
            </Link>
          </div>
          {upcoming.length === 0 ? (
            <div
              style={{
                padding: "28px 20px",
                color: "var(--text-muted)",
                fontSize: "0.875rem",
              }}
            >
              No upcoming deadlines 🎉
            </div>
          ) : (
            upcoming.map((t) => {
              const dl = new Date(t.deadline);
              const isOvr = isBefore(dl, now);
              const isSn = !isOvr && isBefore(dl, addDays(now, 3));
              return (
                <div
                  key={t._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 20px",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <div
                    style={{
                      width: 4,
                      height: 36,
                      borderRadius: 99,
                      flexShrink: 0,
                      background: isOvr
                        ? "#ef4444"
                        : isSn
                          ? "#f59e0b"
                          : "var(--border2)",
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t.title}
                    </div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        marginTop: 2,
                      }}
                    >
                      {t.assignedTo ? (
                        <>
                          <UserAvatar user={t.assignedTo} size={14} />
                          {t.assignedTo.name}
                        </>
                      ) : (
                        "Unassigned"
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <span className={`badge badge-${t.priority}`}>
                      {t.priority}
                    </span>
                    <div
                      style={{
                        fontSize: "0.7rem",
                        marginTop: 3,
                        fontWeight: 600,
                        color: isOvr
                          ? "#ef4444"
                          : isSn
                            ? "#f59e0b"
                            : "var(--text-muted)",
                      }}
                    >
                      {isOvr
                        ? `${formatDistanceToNow(dl)} overdue`
                        : format(dl, "MMM d")}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Recently added */}
        <div className="card">
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              Recently Added
            </span>
            <Link to="/tasks" className="btn btn-secondary btn-sm">
              View All
            </Link>
          </div>
          {recent.length === 0 ? (
            <div
              style={{
                padding: "28px 20px",
                color: "var(--text-muted)",
                fontSize: "0.875rem",
              }}
            >
              No tasks yet.
            </div>
          ) : (
            recent.map((t) => (
              <div
                key={t._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "11px 20px",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <UserAvatar user={t.createdBy} size={34} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.title}
                  </div>
                  <div
                    style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}
                  >
                    by {t.createdBy?.name} ·{" "}
                    {formatDistanceToNow(new Date(t.createdAt), {
                      addSuffix: true,
                    })}
                  </div>
                </div>
                <span className={`badge badge-${t.status}`}>{t.status}</span>
              </div>
            ))
          )}
        </div>
      </div>
      {isAdmin && assigneePerformance.length > 0 && (
        <div className="card card-body" style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}>
            Team Delivery Snapshot
          </div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--text-muted)",
              marginBottom: 14,
            }}
          >
            Assigned workload split into open and completed tasks
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={assigneePerformance}
              layout="vertical"
              margin={{ top: 4, right: 10, left: 10, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                horizontal={false}
              />
              <XAxis
                type="number"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={70}
              />
              <Tooltip content={<ChartTip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: "0.72rem" }}
              />
              <Bar
                dataKey="open"
                name="Open"
                stackId="tasks"
                fill="#f59e0b"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="completed"
                name="Completed"
                stackId="tasks"
                fill="#10b981"
                radius={[0, 6, 6, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* ── Admin only: workload + summary ── */}
      {isAdmin && (
        <div className="grid grid-2" style={{ marginTop: 24 }}>
          {/* Staff workload */}
          {perUser.length > 0 && (
            <div className="card card-body">
              <div
                style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}
              >
                Staff Workload
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--text-muted)",
                  marginBottom: 18,
                }}
              >
                Tasks assigned or created per member
              </div>
              {perUser.map((u, i) => (
                <div
                  key={u.user._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 12,
                  }}
                >
                  <UserAvatar user={u.user} size={30} />
                  <span
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      minWidth: 80,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {u.name}
                  </span>
                  <MiniBar
                    value={u.count}
                    max={maxCount}
                    color={PALETTE[i % PALETTE.length]}
                  />
                  <span
                    style={{
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      color: PALETTE[i % PALETTE.length],
                      minWidth: 22,
                      textAlign: "right",
                    }}
                  >
                    {u.count}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Quick summary */}
          <div className="card card-body">
            <div
              style={{ fontWeight: 700, fontSize: "0.9rem", marginBottom: 2 }}
            >
              Quick Summary
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                marginBottom: 18,
              }}
            >
              Key numbers at a glance
            </div>
            {[
              {
                label: "Total Staff",
                value: users.length,
                color: "#6366f1",
                bg: "#e0e7ff",
                icon: <MdPeople size={16} />,
              },
              {
                label: "Active Tasks",
                value: todo + inProg + review,
                color: "#3b82f6",
                bg: "#dbeafe",
                icon: <MdTask size={16} />,
              },
              {
                label: "Completed",
                value: done,
                color: "#10b981",
                bg: "#d1fae5",
                icon: <MdCheckCircle size={16} />,
              },
              {
                label: "Overdue",
                value: overdue.length,
                color: "#ef4444",
                bg: "#fee2e2",
                icon: <MdPriorityHigh size={16} />,
              },
            ].map((s) => (
              <div
                key={s.label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 9,
                    background: s.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <span style={{ color: s.color, display: "flex" }}>
                    {s.icon}
                  </span>
                </div>
                <span
                  style={{ fontWeight: 600, fontSize: "0.875rem", flex: 1 }}
                >
                  {s.label}
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    color: s.color,
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {s.value}
                </span>
              </div>
            ))}
            {/* Monthly reset reminder */}
            <div
              style={{
                marginTop: 16,
                padding: "11px 14px",
                background: "var(--primary-light)",
                borderRadius: 10,
                border: "1px solid var(--primary)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <MdCalendarToday color="var(--primary)" size={16} />
              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "var(--primary)",
                  }}
                >
                  Monthly Kanban Reset
                </div>
                <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>
                  Done cards auto-hide on the 26th of each month at 12:00 AM
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Dashboard;
